/**
 * The Lynqpod lockup: the hub-and-spoke pod inside a squircle, and the lowercase
 * wordmark.
 *
 * ⚠️ REDRAWN AS SVG FROM THE SUPPLIED ARTWORK, not embedded as the JPEG. The lockup
 * arrived as a 1200×480 JPEG on a white ground, and dropping that into a 24px-tall top
 * bar gives you a soft, artefacted logo sitting in a white box that shows against any
 * surface but pure white. Vector costs nothing, scales, and inherits nothing it should
 * not.
 *
 * COLOURS SAMPLED FROM THE ARTWORK'S OWN PIXELS rather than matched by eye: the gradient
 * runs #0b1020 to #4f46e5, which are --ink and --brand in the stylesheet lynqpod.com
 * actually serves.
 *
 * ⚠️ IT USED TO RUN #0b1225 TO #2d68dc, read off the artwork, and neither value appears
 * anywhere on the site any more. The mark in the application had drifted a whole hue away
 * from the mark on our own home page. If it is changed again, read it off the site's CSS
 * rather than matching it by eye.
 *
 * ⚠️ THE WORDMARK FONT IS AN APPROXIMATION. The original is a geometric sans; this sets
 * it in Inter, which the app already loads, because adding a web font means adding a host
 * to the CSP and a render-blocking request for seven letters. If the exact face matters,
 * the right fix is an SVG of the wordmark with the type converted to outlines, and this
 * component can take it as a <path> without anything else changing.
 */

/**
 * One gradient definition shared by every instance. Duplicated ids across instances
 * would be invalid markup even though browsers tolerate it, so the defs live here and
 * the id is fixed.
 */
const GRAD = 'lynqpod-grad'

export function BrandMark({ size = 28, className = '', tone = 'ink' }:
  { size?: number; className?: string; tone?: 'ink' | 'white' }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} className={className}
         role="img" aria-label="Lynqpod">
      <defs>
        {/* On a dark panel the mark's own gradient runs from near-black, which loses the
            ring entirely. `white` keeps the same shape and lets the panel be the colour. */}
        <linearGradient id={GRAD} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor={tone === 'white' ? '#ffffff' : '#0b1020'} />
          <stop offset="100%" stopColor={tone === 'white' ? '#c7cbfa' : '#4f46e5'} />
        </linearGradient>
      </defs>
      {/* The pod: a squircle outline, not a circle and not a rounded rectangle.
          Proportions measured off the supplied artwork rather than eyeballed: the ring
          stroke, the hub and the end dots are all sized against the mark's own width, so
          it reads as the same logo at 20px as it does at 96px. */}
      <rect x="2.9" y="2.9" width="26.2" height="26.2" rx="8"
            fill="none" stroke={`url(#${GRAD})`} strokeWidth="1.9" />
      {/* Spokes under the dots, so the joins stay clean. */}
      <g stroke={`url(#${GRAD})`} strokeWidth="1.05" strokeLinecap="round">
        <path d="M16 16 16 10M16 16 22 16M16 16 16 22M16 16 10 16" />
      </g>
      <g fill={`url(#${GRAD})`}>
        <circle cx="16" cy="16" r="2.3" />
        <circle cx="16" cy="10" r="1.35" />
        <circle cx="22" cy="16" r="1.35" />
        <circle cx="16" cy="22" r="1.35" />
        <circle cx="10" cy="16" r="1.35" />
      </g>
    </svg>
  )
}

/**
 * Mark plus wordmark. `tone` picks the ink: the gradient on light surfaces, flat white
 * where the lockup sits on the brand colour itself.
 */
export function Brand({ size = 28, tone = 'ink', className = '' }:
  { size?: number; tone?: 'ink' | 'white'; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <BrandMark size={size} tone={tone} />
      <span
        className="text-[1.15rem] font-bold lowercase leading-none tracking-[-0.03em]"
        style={tone === 'white' ? { color: '#fff' } : {
          // A gradient across the word, the way the artwork does it. The fallback colour
          // is the dark end, so it stays readable if a browser refuses the clip.
          color: '#0b1020',
          backgroundImage: 'linear-gradient(95deg, #0b1020 0%, #4f46e5 100%)',
          WebkitBackgroundClip: 'text', backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
        lynqpod
      </span>
    </span>
  )
}
